(() => {
  const controllerKey = 'StepaheadUiLifecycle';
  window[controllerKey]?.destroy?.();

  const components = new Map();
  const mountedRoots = new Map();
  const alpineData = new Map();
  let started = false;
  let waitingForDom = document.readyState === 'loading';

  const rootsWithin = (scope, selector) => {
    const roots = [...(scope.querySelectorAll?.(selector) ?? [])];
    if (scope.matches?.(selector)) roots.unshift(scope);
    return [...new Set(roots)];
  };

  const cleanupRoot = (name, root) => {
    const roots = mountedRoots.get(name);
    roots?.get(root)?.();
    roots?.delete(root);
    if (roots?.size === 0) mountedRoots.delete(name);
  };

  const cleanupComponent = (name) => {
    const roots = mountedRoots.get(name);
    if (!roots) return;
    [...roots.keys()].forEach((root) => cleanupRoot(name, root));
  };

  const cleanupWithin = (scope) => {
    mountedRoots.forEach((roots, name) => {
      [...roots.keys()].forEach((root) => {
        if (root === scope || scope.contains?.(root)) cleanupRoot(name, root);
      });
    });
  };

  const cleanupAll = () => [...mountedRoots.keys()].forEach(cleanupComponent);

  const mountComponentWithin = (component, scope) => {
    const roots = mountedRoots.get(component.name) ?? new Map();
    mountedRoots.set(component.name, roots);

    rootsWithin(scope, component.selector).forEach((root) => {
      if (roots.has(root)) return;
      const result = component.mount(root);
      const cleanup = typeof result === 'function'
        ? result
        : typeof result?.destroy === 'function'
          ? () => result.destroy()
          : () => {};
      roots.set(root, cleanup);
    });
  };

  const mountWithin = (scope = document) => {
    components.forEach((component) => mountComponentWithin(component, scope));
  };

  const remountPage = () => {
    cleanupAll();
    mountWithin(document);
  };

  const unregister = (name) => {
    cleanupComponent(name);
    components.delete(name);
  };

  const register = (component) => {
    if (!component?.name || !component?.selector || typeof component.mount !== 'function') {
      throw new TypeError('UI lifecycle components require name, selector, and mount(root)');
    }
    unregister(component.name);
    components.set(component.name, component);
    if (started) mountComponentWithin(component, document);
    return () => unregister(component.name);
  };

  const installAlpineData = () => {
    if (!window.Alpine?.data) return;
    alpineData.forEach((factory, name) => window.Alpine.data(name, factory));
  };

  const registerAlpineData = (name, factory) => {
    alpineData.set(name, factory);
    installAlpineData();
  };

  const onDomReady = () => {
    waitingForDom = false;
    started = true;
    remountPage();
  };
  const onPageLoad = () => {
    started = true;
    remountPage();
  };
  const onPageBeforeSwap = () => cleanupAll();
  const onSectionLoad = (event) => mountWithin(event.target);
  const onSectionUnload = (event) => cleanupWithin(event.target);

  document.addEventListener('alpine:init', installAlpineData);
  document.addEventListener('astro:page-load', onPageLoad);
  document.addEventListener('astro:before-swap', onPageBeforeSwap);
  document.addEventListener('swup:page:view', onPageLoad);
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);

  if (waitingForDom) document.addEventListener('DOMContentLoaded', onDomReady, { once: true });
  else onDomReady();

  const api = {
    register,
    registerAlpineData,
    mountWithin,
    destroyWithin: cleanupWithin,
    destroy() {
      cleanupAll();
      components.clear();
      alpineData.clear();
      document.removeEventListener('alpine:init', installAlpineData);
      document.removeEventListener('astro:page-load', onPageLoad);
      document.removeEventListener('astro:before-swap', onPageBeforeSwap);
      document.removeEventListener('swup:page:view', onPageLoad);
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      if (waitingForDom) document.removeEventListener('DOMContentLoaded', onDomReady);
      waitingForDom = false;
      started = false;
    },
  };

  window[controllerKey] = api;
})();
